import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const CreateIssue = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'Medium',
    status: 'Open',
    assigned_to: ''
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users');
      setUsers(response.data);
    } catch (error) {
      // Users endpoint might be admin-only, ignore error
      console.log('Could not fetch users for assignment');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        assigned_to: formData.assigned_to || null
      };

      const response = await axios.post('/issues', submitData);
      
      // Redirect to the newly created issue
      navigate(`/issues/${response.data.id}`);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create issue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-content">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Create New Issue</h1>
        <p className="text-gray-600">Report a new bug or request a feature</p>
      </div>

      <div className="card">
        <div className="card-body">
          {error && (
            <div className="alert alert-danger mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="title">
                Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                className="form-control"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Enter a clear, concise title for the issue"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="description">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                className="form-control textarea"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder="Provide a detailed description of the issue, including steps to reproduce if it's a bug"
                rows={6}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label" htmlFor="priority">
                  Priority
                </label>
                <select
                  id="priority"
                  name="priority"
                  className="form-control form-select"
                  value={formData.priority}
                  onChange={handleChange}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
                <div className="text-sm text-gray-600 mt-1">
                  {formData.priority === 'Low' && 'Minor issues that don\'t affect functionality'}
                  {formData.priority === 'Medium' && 'Standard issues that should be addressed'}
                  {formData.priority === 'High' && 'Important issues affecting user experience'}
                  {formData.priority === 'Critical' && 'Urgent issues that block functionality'}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="status">
                  Initial Status
                </label>
                <select
                  id="status"
                  name="status"
                  className="form-control form-select"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="Open">Open</option>
                  <option value="In Progress">In Progress</option>
                </select>
              </div>
            </div>

            {users.length > 0 && (
              <div className="form-group">
                <label className="form-label" htmlFor="assigned_to">
                  Assign To
                </label>
                <select
                  id="assigned_to"
                  name="assigned_to"
                  className="form-control form-select"
                  value={formData.assigned_to}
                  onChange={handleChange}
                >
                  <option value="">Unassigned</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.role})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    Creating Issue...
                  </>
                ) : (
                  'Create Issue'
                )}
              </button>
              
              <button
                type="button"
                onClick={() => navigate('/issues')}
                className="btn btn-outline"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Tips Card */}
      <div className="card mt-6">
        <div className="card-header">
          <h3 className="card-title">Tips for Writing Good Issues</h3>
        </div>
        <div className="card-body">
          <ul className="space-y-2 text-sm text-gray-600">
            <li>• <strong>Be specific:</strong> Use clear, descriptive titles that summarize the issue</li>
            <li>• <strong>Provide details:</strong> Include steps to reproduce, expected vs actual behavior</li>
            <li>• <strong>Add context:</strong> Mention browser, OS, or relevant environment details</li>
            <li>• <strong>Set appropriate priority:</strong> Help the team understand urgency</li>
            <li>• <strong>Assign appropriately:</strong> If you know who should handle it, assign them</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CreateIssue; 